import {Component, OnDestroy, OnInit} from '@angular/core';
import {ConfirmationService, LazyLoadEvent, MenuItem} from 'primeng/api';
import {select, Store} from '@ngrx/store';
import {ActivatedRoute, Router} from '@angular/router';
import {map, switchMap} from 'rxjs/operators';
import {LazyLoadMeta} from '../../../../shared/model/lazy-load-meta.model';
import {BaseComponent} from '../../../../shared/base/base.component';
import {ProxyService} from '../../../../shared/services/proxy.service';
import {GlobalService} from '../../../../shared/services/global.service';
import {currentStateGetCurrentSubject} from '../../../../store/current-state/current-state.selector';
import {CustomPermissionsService} from '../../../../shared/services/custom-permissions.service';
import {TranslateService} from '@ngx-translate/core';
import {forkJoin} from 'rxjs';
import {TagGoodPractice} from '../../../../../generated/graphql';
import {getGoodPractices} from '../store/tag-good-practice.selector';
import {MediaService} from '../../../../shared/services/media.service';
import {EnumsHelper} from '../../../../shared/helpers/enums-helper.service';
import {EntityTypes, TagTypes} from '../../../../shared/enums/entity-enums';
import {StatisticsService} from '../../../../shared/services/statistics.service';
import * as SendActions from '../../../../forms/send-tag-form/store/send-form.actions';
import {AppState} from '../../../../store/app.reducer';
import {SetBreadcrumb, UpdateToolbarButtons} from '../../../../fixed-toolbar/fixed-toolbar.action';
import {DialogShow} from '../../../../forms/tags/good-practice-create/store/good-practice-create-form.actions';
import {TagGoodPracticeDelete, TagGoodPracticesFetch} from '../store/tag-good-practice.actions';

@Component({
    selector: 'app-tag-good-practice-list',
    templateUrl: './tag-good-practice-list.component.html',
    styleUrls: ['./tag-good-practice-list.component.css']
})
export class TagGoodPracticeListComponent extends BaseComponent implements OnInit, OnDestroy {

    breadcrumbItems: MenuItem[];

    public goodPractices: any;
    public totalRecords: any;
    public layoutContentHeight: number;
    public sortColumns: any[];
    public filters: any;
    public presetFilters: any[] = [];

    constructor(protected store: Store<AppState>,
                public gs: GlobalService,
                private router: Router,
                private route: ActivatedRoute,
                private confirmationService: ConfirmationService,
                private proxyService: ProxyService,
                private ps: CustomPermissionsService,
                private translate: TranslateService,
                private media: MediaService,
                private enumsHelper: EnumsHelper,
                private statisticsService: StatisticsService,
    ) {
        super(store, gs);
    }

    ngOnInit(): void {
        super.ngOnInit();

        const presetStateFilter = this.statisticsService.getTagFilterState(TagTypes.TAG_GOOD_PRACTICE);
        if (presetStateFilter !== undefined) {
            this.presetFilters.push({field: 'state', value: presetStateFilter});
            this.statisticsService.clearTagFilterState(TagTypes.TAG_GOOD_PRACTICE);
        }

        forkJoin([
            this.translate.get('good_practice.title_m'),
        ]).subscribe(result => {
            this.breadcrumbItems = [];
            this.breadcrumbItems.push({label: result[0], routerLink: '/good-practice'});
            this.store.dispatch(new SetBreadcrumb(this.breadcrumbItems));
        });

        this.subs.sink = this.store.pipe(select(currentStateGetCurrentSubject)).subscribe((currentSubject) => {
            this.subs.sink = this.ps.permissionsLoaded().subscribe((loaded) => {
                if (loaded) {
                    this.subs.sink = this.ps.getNgxPermissions$().subscribe(() => {
                        this.ps.hasPermission('createGoodPractice').then((permission) => {
                            this.buttons = [...this.buttons];
                            if (permission) {
                                this.buttons.push(
                                    {
                                        id: 'createGoodPracticeButton',
                                        name: 'good_practice.create',
                                        action: 'onCreateGoodPractice',
                                        class: 'p-button-primary',
                                        icon: 'pi pi-plus',
                                        disabled: false,
                                    }
                                );
                            }
                            this.store.dispatch(new UpdateToolbarButtons(this.buttons));
                        });

                        this.subs.sink = this.store.select(getGoodPractices).pipe(
                            map(goodPracticeState => {
                                return {
                                    goodPractices: goodPracticeState.goodPractices,
                                    totalRows: goodPracticeState.totalRows,
                                };
                            }),
                            switchMap(({
                                           goodPractices: goodPractices,
                                           totalRows: totalRows,
                                       }) => {
                                if (goodPractices.length > 0) {

                                    const promises = [];

                                    goodPractices.forEach(record => {
                                        promises.push(this.ps.hasProjectPermission(record.project, 'updateGoodPractice'));
                                        promises.push(this.ps.hasProjectPermission(record.project, 'deleteGoodPractice'));
                                    });

                                    return Promise.all(
                                        promises
                                    ).then(results => {
                                        goodPractices = goodPractices.map((goodPractice: TagGoodPractice, index) => {
                                            const idx1 = (index * 2);
                                            const idx2 = (index * 2) + 1;

                                            const updatePerm = results[idx1];
                                            const deletePerm = results[idx2];

                                            const contextMenu = [
                                                {
                                                    label: this.translate.instant('general.send'),
                                                    icon: 'pi pi-envelope',
                                                    command: (event: Event) => {
                                                        this.onEmail(goodPractice.id, goodPractice.identifier);
                                                    }
                                                }
                                            ];

                                            if (deletePerm) {
                                                contextMenu.push({
                                                    label: this.translate.instant('general.delete'),
                                                    icon: 'pi pi-trash',
                                                    command: (event: Event) => {
                                                        this.onDelete(goodPractice.id, goodPractice.identifier);
                                                    }
                                                });
                                            }

                                            return {
                                                ...goodPractice,
                                                img: goodPractice.images.length > 0 ?
                                                    this.media.getMediaLink(goodPractice.images[0]) :
                                                    this.media.getNoImageLink(),
                                                thumb: goodPractice.images.length > 0 ?
                                                    this.media.getMediaThumbLink(goodPractice.images[0]) :
                                                    this.media.getNoImageLink(),
                                                contextMenu,
                                                navLink: ['/', 'good-practice', 'browse', goodPractice.link]
                                            };
                                        });

                                        return {
                                            goodPractices,
                                            totalRows,
                                        };
                                    });
                                } else {
                                    return new Promise((resolve) => {
                                        resolve({
                                            goodPractices: [],
                                            totalRows: 0,
                                        });
                                    });
                                }

                            })).subscribe(
                            ({
                                 goodPractices: goodPractices,
                                 totalRows: totalRows,
                             }) => {
                                this.goodPractices = goodPractices;
                                this.totalRecords = totalRows;
                            });
                    });
                }
            });
        });

        this.subs.sink = forkJoin([
            this.translate.get('general_tag.occurred_at'),
            this.translate.get('general_tag.created_at'),
            this.translate.get('general_tag.state.title'),
        ]).subscribe(result => {
            this.sortColumns = [
                { label: result[0], value: {dbField: 'tg.occurred_at', name: result[0]} },
                { label: result[1], value: {dbField: 'tg.created_at', name: result[1]} },
                { label: result[2], value: {dbField: 'tg.state_id', name: result[2]} },
            ];
        });

        this.filters = [
            {
                field: 'state',
                dbField: 'tg.state_id',
                matchMode: 'in',
                values: this.enumsHelper.statesMulti,
                label: 'general_tag.state.title',
                width: '250px'
            }
        ];
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    onCreateGoodPractice() {
        this.store.dispatch(new DialogShow());
    }

    fetchGoodPractices(event: object): void {
        this.store.dispatch(new TagGoodPracticesFetch(event));
    }

    loadLazy(event: LazyLoadEvent) {
        const searchData = LazyLoadMeta.fromData(event);
        this.fetchGoodPractices(searchData);
    }

    onEdit(link: string) {
        this.router.navigate(['/', 'browse', link]);
    }

    onDelete(id: number, identifier: string) {
        forkJoin([this.translate.get('good_practice.delete_confirm_message')]).subscribe(result => {
            this.confirmationService.confirm({
                message: result[0] + ' [' + identifier + ']',
                accept: () => {
                    this.store.dispatch(new TagGoodPracticeDelete({goodPracticeId: id}));
                }
            });
        });
    }

    private onEmail(id: number, identifier: string) {
        this.store.dispatch(new SendActions.DialogShow({type: EntityTypes.TAG_GOOD_PRACTICE, id, identifier}));
    }
}
